import { api } from "@glasshalf/api-client";
import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ConfirmImpactDialog } from "../../components/ConfirmImpactDialog";
import { DataTable } from "../../components/DataTable";
import { SavedViews, type SavedView } from "../../components/SavedViews";
import { StatusBadge } from "../../components/StatusBadge";
import { formatNumber } from "../../lib/format";
import { inventoryRows, locations } from "../../lib/selectors";
import type { AnyRecord, AppData } from "../../lib/types";
import { BarcodeWorkspace } from "../barcode/BarcodePage";

export function StoresPage({ data }: { data: AppData }) {
  const [params, setParams] = useSearchParams();
  const activeView = params.get("view") || "stock";
  const stores = storeRows(data);
  const defaultStoreCode = String(stores[0]?.code || "");
  const salesMovements = storeSalesMovements(data, stores.map((store) => String(store.code)));
  const totalStoreStock = stores.reduce((sum, store) => sum + Number(store.onHandTotal || 0), 0);
  const views: SavedView[] = [
    { key: "stock", label: "매장 재고", count: stores.length },
    { key: "receive", label: "매장 입고 확인", count: stores.filter((store) => Number(store.inboundPlanned || 0) > 0).length },
    { key: "sales", label: "오프라인 판매 차감", count: salesMovements.length },
    { key: "pos", label: "POS 동기화", count: 0 }
  ];
  const columns: ColumnDef<AnyRecord>[] = [
    {
      accessorKey: "name",
      header: "매장",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.name}</strong>
          <span>{row.original.code}</span>
        </div>
      ),
      size: 220
    },
    { accessorKey: "onHandTotal", header: "실재고", cell: ({ row }) => <span className="num">{formatNumber(row.original.onHandTotal || 0)}</span> },
    { accessorKey: "skuCount", header: "SKU", cell: ({ row }) => <span className="num">{formatNumber(row.original.skuCount || 0)}</span> },
    { accessorKey: "lowStockCount", header: "저재고", cell: ({ row }) => <span className="num">{formatNumber(row.original.lowStockCount || 0)}</span> },
    { accessorKey: "posStatus", header: "POS", cell: ({ row }) => <StatusBadge tone={row.original.posTone}>{row.original.posStatus}</StatusBadge> },
    { id: "action", header: "작업", cell: ({ row }) => <Link className="table-link" to={`/inventory?mode=transfer&store=${encodeURIComponent(String(row.original.code))}`}>위치 이동</Link> }
  ];

  return (
    <div className="page-stack">
      <section className="toolbar-panel">
        <SavedViews views={views} active={activeView} onChange={(view) => setParams(view === "stock" ? {} : { view })} />
        <div className="stores-quick-actions">
          <Link className={activeView === "sales" ? "is-active" : undefined} to="/stores?view=sales">오프라인 판매 차감</Link>
          <Link className={activeView === "barcode" ? "is-active" : undefined} to={storeBarcodePath(defaultStoreCode)}>
            <Camera size={14} />
            Barcode
          </Link>
        </div>
      </section>

      {activeView === "sales" ? (
        <StoreSalesIssuePanel stores={stores} rows={inventoryRows(data)} salesMovements={salesMovements} />
      ) : activeView === "barcode" ? (
        <BarcodeWorkspace
          key={defaultStoreCode}
          data={data}
          title="매장 바코드 작업"
          description="카메라 또는 직접 입력으로 매장 출고 작업을 미리보기 후 기록합니다."
          initialOperation="issue"
          initialFromLocationCode={defaultStoreCode}
        />
      ) : (
        <section className="store-summary-grid">
          <div className="panel">
            <div className="panel-head">
              <div>
                <h2>매장 재고</h2>
                <p>오프라인 매장에 있는 실제 재고와 확인이 필요한 SKU를 봅니다.</p>
              </div>
              <StatusBadge tone={stores.length ? "info" : "warning"}>{formatNumber(stores.length)} 매장</StatusBadge>
            </div>
            <div className="metric-list">
              <div><span>매장 실재고</span><strong>{formatNumber(totalStoreStock)}개</strong></div>
              <div><span>저재고 SKU</span><strong>{formatNumber(stores.reduce((sum, store) => sum + Number(store.lowStockCount || 0), 0))}개</strong></div>
            </div>
          </div>
          <div className="panel">
            <div className="panel-head">
              <div>
                <h2>POS 동기화</h2>
                <p>POS 연동은 설정, 매장 판매 결과 확인은 Stores에서 봅니다.</p>
              </div>
              <StatusBadge tone="warning">준비 중</StatusBadge>
            </div>
            <div className="sync-list">
              <Link className="sync-row" to={storeBarcodePath(defaultStoreCode)}>
                <div>
                  <strong>현장 스캔</strong>
                  <span>모바일 카메라로 판매 차감</span>
                </div>
                <strong>스캔</strong>
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>오프라인 매장 목록</h2>
            <p>본사창고에서 매장으로 보내는 작업은 위치 이동으로, 매장 판매는 판매 차감으로 기록합니다.</p>
          </div>
        </div>
        <DataTable columns={columns} data={stores} getRowId={(row) => String(row.code)} emptyTitle="등록된 오프라인 매장이 없습니다." />
      </section>
    </div>
  );
}

function StoreSalesIssuePanel({ stores, rows, salesMovements }: { stores: AnyRecord[]; rows: AnyRecord[]; salesMovements: AnyRecord[] }) {
  const queryClient = useQueryClient();
  const [barcode, setBarcode] = useState("");
  const [selectedRow, setSelectedRow] = useState<AnyRecord | null>(null);
  const [storeCode, setStoreCode] = useState(String(stores[0]?.code || ""));
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState("오프라인 판매");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lookup = useMutation({ mutationFn: () => api.lookupBarcode(barcode.trim()) });
  const payload = (dryRun: boolean) => ({
    barcode: barcode.trim(),
    operation: "issue" as const,
    quantity,
    fromLocationCode: storeCode,
    reason,
    dryRun
  });
  const preview = useMutation({
    mutationFn: () => api.runBarcodeOperation(payload(true)),
    onSuccess: () => setConfirmOpen(true)
  });
  const runIssue = useMutation({
    mutationFn: () => api.runBarcodeOperation(payload(false)),
    onSuccess: async () => {
      setConfirmOpen(false);
      setBarcode("");
      setSelectedRow(null);
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  const candidates = useMemo(() => storeSkuCandidates(rows, barcode), [rows, barcode]);
  const exactInput = useMemo(() => exactStoreSkuInput(rows, barcode), [rows, barcode]);
  const needsCandidateSelection = Boolean(barcode.trim().length >= 2 && candidates.length > 0 && !selectedRow && !exactInput);
  const item = (lookup.data?.item || preview.data?.item || runIssue.data?.item || (selectedRow ? storeSkuItem(selectedRow, stores) : null)) as AnyRecord | null;
  const previewLine = useMemo(() => {
    const lines = preview.data?.preview && typeof preview.data.preview === "object" ? (preview.data.preview as AnyRecord).lines : [];
    return Array.isArray(lines) ? lines[0] as AnyRecord | undefined : undefined;
  }, [preview.data]);
  const error = lookup.error || preview.error || runIssue.error;
  const canPreview = Boolean(barcode.trim() && storeCode && quantity > 0 && !needsCandidateSelection);

  function updateBarcode(value: string) {
    setBarcode(value);
    setSelectedRow(null);
    lookup.reset();
    preview.reset();
  }

  function selectCandidate(row: AnyRecord) {
    const code = exactSkuLookupCode(row);
    setBarcode(code);
    setSelectedRow(row);
    lookup.reset();
    preview.reset();
  }

  return (
    <section className="panel store-sales-panel">
      <div className="panel-head">
        <div>
          <h2>오프라인 판매 차감</h2>
          <p>POS 미연동 또는 수동 판매 건을 매장 실제 재고에서 차감합니다.</p>
        </div>
        <div className="panel-actions">
          <StatusBadge tone={salesMovements.length ? "info" : "default"}>최근 {formatNumber(salesMovements.length)}건</StatusBadge>
        </div>
      </div>
      <div className="store-sales-layout">
        <div className="store-sales-form">
          <label>
            매장
            <select value={storeCode} onChange={(event) => setStoreCode(event.target.value)}>
              {stores.map((store) => <option key={String(store.code)} value={String(store.code)}>{String(store.name || store.code)}</option>)}
            </select>
          </label>
          <label>
            바코드 / SKU
            <div className="inline-control">
              <input aria-label="바코드 / SKU" value={barcode} onChange={(event) => updateBarcode(event.target.value)} placeholder="바코드, SKU, readable alias" />
              <button type="button" onClick={() => lookup.mutate()} disabled={!barcode.trim() || lookup.isPending}>
                <Search size={15} />
                조회
              </button>
            </div>
          </label>
          {candidates.length && !selectedRow ? (
            <div className="store-sales-candidates" aria-label="SKU 후보">
              {candidates.map((candidate) => (
                <button type="button" key={String(candidate.sku)} onClick={() => selectCandidate(candidate)}>
                  <span>
                    <strong>{String(candidate.productName || candidate.readableAlias || candidate.sku)}</strong>
                    <small>{String(candidate.readableAlias || candidate.sku)} · {String(candidate.colorwayName || "-")} / {String(candidate.sizeName || "-").toUpperCase()}</small>
                  </span>
                  <span>{formatNumber(locationQuantity(candidate, storeCode))}개</span>
                </button>
              ))}
            </div>
          ) : null}
          <label>
            수량
            <input type="number" min={1} value={quantity} onChange={(event) => setQuantity(Math.max(1, Number(event.target.value || 1)))} />
          </label>
          <label>
            사유
            <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="예: 성수동 매장 판매" />
          </label>
          {error ? <p className="form-error">{errorMessage(error)}</p> : null}
          {needsCandidateSelection ? <p className="field-hint">후보를 선택하면 정확한 바코드/SKU로 차감할 수 있습니다.</p> : null}
          <button type="button" className="primary" onClick={() => preview.mutate()} disabled={!canPreview || preview.isPending}>
            차감 미리보기
          </button>
        </div>
        <div className="store-sales-preview">
          <h3>차감 대상</h3>
          {item ? (
            <>
              <div className="cell-primary">
                <strong>{String(item.productName || item.readableAlias || item.sku)}</strong>
                <span>{String(item.readableAlias || item.sku || barcode)}</span>
              </div>
              <div className="metric-list">
                <div><span>전체 실재고</span><strong>{formatNumber(item.onHandTotal || 0)}개</strong></div>
                <div><span>온라인 할당</span><strong>{formatNumber(item.allocatedTotal || 0)}개</strong></div>
              </div>
              <div className="sync-list">
                {Array.isArray(item.balances) ? item.balances.map((balance: AnyRecord) => (
                  <div className="sync-row" key={String(balance.locationCode)}>
                    <div>
                      <strong>{String(balance.locationName || balance.locationCode)}</strong>
                      <span>{String(balance.locationCode)}</span>
                    </div>
                    <strong>{formatNumber(balance.quantity || 0)}개</strong>
                  </div>
                )) : null}
              </div>
            </>
          ) : (
            <p className="muted">바코드나 SKU를 조회하면 상품과 위치별 재고가 표시됩니다.</p>
          )}
        </div>
      </div>
      <ConfirmImpactDialog
        open={confirmOpen}
        title="오프라인 판매 차감 확인"
        currentValue={previewLine ? `${previewLine.locationName || previewLine.locationCode} ${formatNumber(previewLine.beforeQuantity || 0)}개` : "-"}
        nextValue={previewLine ? `${formatNumber(previewLine.afterQuantity || 0)}개` : `-${formatNumber(quantity)}개`}
        externalImpact="Cafe24와 POS 수량은 직접 변경하지 않고 백오피스 매장 실제 재고만 차감합니다."
        auditTrail="매장, SKU, 바코드, 차감 수량, 사유, 전후 수량이 재고 원장과 감사 로그에 남습니다."
        reason={reason}
        confirmLabel="판매 차감 기록"
        tone="danger"
        busy={runIssue.isPending}
        onReasonChange={setReason}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => runIssue.mutate()}
      />
    </section>
  );
}

function storeRows(data: AppData): AnyRecord[] {
  const rows = inventoryRows(data);
  return locations(data)
    .filter((location) => location.active !== false && String(location.locationType || location.channelType || "").includes("store"))
    .map((location) => {
      const code = String(location.code || "");
      const storeBalances = rows.map((row) => {
        return {
          quantity: locationQuantity(row, code),
          sku: row.sku
        };
      }).filter((item) => item.quantity > 0);
      return {
        code,
        name: String(location.name || code),
        onHandTotal: storeBalances.reduce((sum, item) => sum + item.quantity, 0),
        skuCount: storeBalances.length,
        lowStockCount: storeBalances.filter((item) => item.quantity <= 2).length,
        inboundPlanned: 0,
        posStatus: "준비 중",
        posTone: "warning"
      };
    });
}

function storeBarcodePath(storeCode: string): string {
  const base = "/stores?view=barcode&operation=issue";
  return storeCode ? `${base}&fromLocation=${encodeURIComponent(storeCode)}` : base;
}

function storeSalesMovements(data: AppData, storeCodes: string[]): AnyRecord[] {
  const storeCodeSet = new Set(storeCodes);
  const movements = Array.isArray(data.inventory?.movements) ? data.inventory.movements : [];
  return movements.filter((movement) => {
    const type = String(movement.type || movement.movementType || "").toUpperCase();
    const from = String(movement.fromLocationCode || movement.from_location_code || "");
    return type === "OUTBOUND" && storeCodeSet.has(from);
  });
}

function storeSkuCandidates(rows: AnyRecord[], query: string): AnyRecord[] {
  const keyword = query.trim().toLowerCase();
  if (keyword.length < 2) return [];
  return rows
    .filter((row) => storeSkuSearchText(row).includes(keyword))
    .slice(0, 8);
}

function exactStoreSkuInput(rows: AnyRecord[], query: string): boolean {
  const keyword = query.trim().toLowerCase();
  if (!keyword) return false;
  return rows.some((row) => [row.barcode, row.gtin, row.sku, row.readableAlias]
    .map((value) => String(value || "").trim().toLowerCase())
    .includes(keyword));
}

function storeSkuSearchText(row: AnyRecord): string {
  return [
    row.barcode,
    row.gtin,
    row.sku,
    row.readableAlias,
    row.productName,
    row.productKey,
    row.productCode,
    row.seasonCode,
    row.categoryName,
    row.colorwayName,
    row.colorwayCode,
    row.sizeName,
    row.sizeCode
  ].map((value) => String(value || "").toLowerCase()).join(" ");
}

function exactSkuLookupCode(row: AnyRecord): string {
  return String(row.barcode || row.gtin || row.sku || row.readableAlias || "").trim();
}

function storeSkuItem(row: AnyRecord, storeLocations: AnyRecord[]): AnyRecord {
  const locationNames = new Map(storeLocations.map((location) => [String(location.code), String(location.name || location.code)]));
  return {
    ...row,
    balances: Array.isArray(row.balances)
      ? row.balances
      : Object.entries(row.balances || {}).map(([locationCode, quantity]) => ({
        locationCode,
        locationName: locationNames.get(locationCode) || locationCode,
        quantity
      }))
  };
}

function locationQuantity(row: AnyRecord, code: string): number {
  if (Array.isArray(row.balances)) {
    const balance = row.balances.find((item: AnyRecord) => String(item.locationCode) === code);
    return Number(balance?.quantity || 0);
  }
  if (row.balances && typeof row.balances === "object") return Number(row.balances[code] || 0);
  return 0;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "요청 처리에 실패했습니다.";
}
